import { create } from "zustand";
import { combine } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import {
  computeMassPropertiesFromCurrentScene,
  type MassPropertiesRow,
} from "../physics/massPropertiesProbe";
import type { Vector3 } from "../types/scene";
import { usePhysicsStore } from "./physicsStore";
import { useSceneStore } from "./sceneStore";

export type MassPropertiesStatus = "idle" | "computing" | "ready" | "error";
const AUTO_RECOMPUTE_DEBOUNCE_MS = 900;

type MassPropertiesState = {
  status: MassPropertiesStatus;
  isStale: boolean;
  totalMass: number | null;
  totalCom: Vector3 | null;
  bodyCount: number;
  rows: MassPropertiesRow[];
  computedAt: number | null;
  error: string | null;
  autoRecomputeQueued: boolean;
  showRobotComMarker: boolean;
  showBodyComMarkers: boolean;
};

type MassPropertiesActions = {
  compute: () => Promise<void>;
  markStale: () => void;
  clear: () => void;
  setComMarkerVisibility: (target: "robot" | "body", visible: boolean) => void;
};

const initialState = (): MassPropertiesState => ({
  status: "idle",
  isStale: false,
  totalMass: null,
  totalCom: null,
  bodyCount: 0,
  rows: [],
  computedAt: null,
  error: null,
  autoRecomputeQueued: false,
  showRobotComMarker: false,
  showBodyComMarkers: false,
});

const selectSceneMassSignature = () => {
  const scene = useSceneStore.getState();
  return {
    bodies: scene.bodies,
    worldBodies: scene.worldBodies,
    meshes: scene.meshes,
    hfields: scene.hfields,
    mjcfCompiler: scene.mjcfCompiler,
    mjcfStatistic: scene.mjcfStatistic,
  };
};

const sameSceneMassSignature = (
  a: ReturnType<typeof selectSceneMassSignature>,
  b: ReturnType<typeof selectSceneMassSignature>
) =>
  a.bodies === b.bodies &&
  a.worldBodies === b.worldBodies &&
  a.meshes === b.meshes &&
  a.hfields === b.hfields &&
  a.mjcfCompiler === b.mjcfCompiler &&
  a.mjcfStatistic === b.mjcfStatistic;

const sceneHasMassBodies = (): boolean =>
  Object.keys(useSceneStore.getState().bodies || {}).length > 0;

let sceneMassRevision = 0;

const useInternalMassPropertiesStore = create<
  MassPropertiesState & { actions: MassPropertiesActions }
>()(
  combine(initialState(), (set, get) => ({
    actions: {
      compute: async () => {
        if (get().status === "computing") return;
        if (!sceneHasMassBodies()) {
          clearAutoRecomputeTimer();
          set((state) => ({
            ...initialState(),
            showRobotComMarker: state.showRobotComMarker,
            showBodyComMarkers: state.showBodyComMarkers,
          }));
          return;
        }
        const computeRevision = sceneMassRevision;
        set({ status: "computing", autoRecomputeQueued: false, error: null });
        try {
          const result = await computeMassPropertiesFromCurrentScene();
          const changedDuringCompute = sceneMassRevision !== computeRevision;
          set({
            status: "ready",
            isStale: changedDuringCompute,
            totalMass: result.totalMass,
            totalCom: result.totalCom,
            bodyCount: result.bodyCount,
            rows: result.rows,
            computedAt: result.computedAt,
            error: null,
            autoRecomputeQueued: changedDuringCompute,
          });
          if (changedDuringCompute) scheduleAutoRecompute();
        } catch (error) {
          set({
            status: "error",
            autoRecomputeQueued: false,
            error: String(error instanceof Error ? error.message : error),
          });
        }
      },
      markStale: () => {
        if (!sceneHasMassBodies()) {
          clearAutoRecomputeTimer();
          set((state) => ({
            ...initialState(),
            showRobotComMarker: state.showRobotComMarker,
            showBodyComMarkers: state.showBodyComMarkers,
          }));
          return;
        }
        const status = get().status;
        if (status === "ready" || status === "computing") {
          set({ isStale: true, autoRecomputeQueued: true });
          scheduleAutoRecompute();
        }
      },
      clear: () => {
        clearAutoRecomputeTimer();
        set(initialState());
      },
      setComMarkerVisibility: (target, visible) => {
        if (target === "robot") {
          set({ showRobotComMarker: visible });
          return;
        }
        set({ showBodyComMarkers: visible });
      },
    },
  }))
);

let autoRecomputeTimer: number | null = null;

function clearAutoRecomputeTimer(): void {
  if (!autoRecomputeTimer) return;
  window.clearTimeout(autoRecomputeTimer);
  autoRecomputeTimer = null;
}

function scheduleAutoRecompute(): void {
  if (typeof window === "undefined") return;
  clearAutoRecomputeTimer();
  autoRecomputeTimer = window.setTimeout(() => {
    autoRecomputeTimer = null;
    const state = useInternalMassPropertiesStore.getState();
    if (!state.isStale) {
      useInternalMassPropertiesStore.setState({ autoRecomputeQueued: false });
      return;
    }
    if (state.status === "computing") {
      scheduleAutoRecompute();
      return;
    }
    if (usePhysicsStore.getState().isRunning || usePhysicsStore.getState().isPaused) {
      useInternalMassPropertiesStore.setState({ autoRecomputeQueued: false });
      return;
    }
    if (!sceneHasMassBodies()) {
      useInternalMassPropertiesStore.setState((state) => ({
        ...initialState(),
        showRobotComMarker: state.showRobotComMarker,
        showBodyComMarkers: state.showBodyComMarkers,
      }));
      return;
    }
    void state.actions.compute();
  }, AUTO_RECOMPUTE_DEBOUNCE_MS);
}

let previousSceneMassSignature = selectSceneMassSignature();
useSceneStore.subscribe(() => {
  const next = selectSceneMassSignature();
  if (!sameSceneMassSignature(previousSceneMassSignature, next)) {
    previousSceneMassSignature = next;
    sceneMassRevision += 1;
    useInternalMassPropertiesStore.getState().actions.markStale();
  }
});

export const useMassPropertiesStatus = () =>
  useInternalMassPropertiesStore((state) => state.status);
export const useMassPropertiesIsStale = () =>
  useInternalMassPropertiesStore((state) => state.isStale);
export const useMassPropertiesAutoRecomputeQueued = () =>
  useInternalMassPropertiesStore((state) => state.autoRecomputeQueued);
export const useMassPropertiesSummary = () =>
  useInternalMassPropertiesStore(
    useShallow((state) => ({
      totalMass: state.totalMass,
      totalCom: state.totalCom,
      bodyCount: state.bodyCount,
      computedAt: state.computedAt,
      error: state.error,
    }))
  );
export const useMassPropertiesRows = () => useInternalMassPropertiesStore((state) => state.rows);
export const useMassPropertiesRobotComMarkerVisible = () =>
  useInternalMassPropertiesStore((state) => state.showRobotComMarker);
export const useMassPropertiesBodyComMarkersVisible = () =>
  useInternalMassPropertiesStore((state) => state.showBodyComMarkers);
export const useMassPropertiesActions = () =>
  useInternalMassPropertiesStore((state) => state.actions);
